import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    Button,
    FormControlLabel,
    Checkbox,
    Chip,
    LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCustomerById, updateCustomer } from '../services/customers';
import { getEffectiveAccountId } from '../utils/accountId';

const FormContainer = styled(Card)(({ theme }) => ({
    background: 'white',
    borderRadius: '20px',
    padding: '30px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
    marginBottom: '20px',
    position: "relative",
    width: '100%',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    [theme.breakpoints.down('sm')]: {
        borderRadius: '12px',
        padding: '16px',
    },
}));

const QuestionCard = styled(Box)(({ theme }) => ({
    marginBottom: "20px",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    backgroundColor: "#fafafa",
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    [theme.breakpoints.down('sm')]: {
        padding: "16px",
        marginBottom: "16px",
    },
}));

const ScoreDisplay = styled(Box)(({ theme }) => ({
    background: 'white',
    padding: '20px',
    borderRadius: '10px',
    textAlign: 'center',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
        padding: '16px',
    },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
    padding: '12px 30px',
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    color: 'white',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: '10px 20px',
        fontSize: '14px',
        width: '100%',
    },
}));

const ArmBadge = styled(Chip)(({ arm }) => ({
    padding: '5px 15px',
    borderRadius: '20px',
    fontSize: '0.85em',
    fontWeight: 600,
    color: 'white',
    background: arm === 'test' ? '#4caf50' : arm === 'control' ? '#ff9800' : '#9e9e9e',
}));

const hadsQuestions = [
    // Anxiety questions (odd numbers)
    {
        id: 1,
        type: 'anxiety',
        question: 'I feel tense or wound up:',
        options: [
            { value: 3, label: 'Most of the time' },
            { value: 2, label: 'A lot of the time' },
            { value: 1, label: 'From time to time, occasionally' },
            { value: 0, label: 'Not at all' },
        ],
    },
    // Depression questions (even numbers)
    {
        id: 2,
        type: 'depression',
        question: 'I still enjoy the things I used to enjoy:',
        options: [
            { value: 0, label: 'Definitely as much' },
            { value: 1, label: 'Not quite so much' },
            { value: 2, label: 'Only a little' },
            { value: 3, label: 'Hardly at all' },
        ],
    },
    {
        id: 3,
        type: 'anxiety',
        question: 'I get a sort of frightened feeling as if something awful is about to happen:',
        options: [
            { value: 3, label: 'Very definitely and quite badly' },
            { value: 2, label: 'Yes, but not too badly' },
            { value: 1, label: "A little, but it doesn't worry me" },
            { value: 0, label: 'Not at all' },
        ],
    },
    {
        id: 4,
        type: 'depression',
        question: 'I can laugh and see the funny side of things:',
        options: [
            { value: 0, label: 'As much as I always could' },
            { value: 1, label: 'Not quite so much now' },
            { value: 2, label: 'Definitely not so much now' },
            { value: 3, label: 'Not at all' },
        ],
    },
    {
        id: 5,
        type: 'anxiety',
        question: 'Worrying thoughts go through my mind:',
        options: [
            { value: 3, label: 'A great deal of the time' },
            { value: 2, label: 'A lot of the time' },
            { value: 1, label: 'From time to time, but not too often' },
            { value: 0, label: 'Only occasionally' },
        ],
    },
    {
        id: 6,
        type: 'depression',
        question: 'I feel cheerful:',
        options: [
            { value: 3, label: 'Not at all' },
            { value: 2, label: 'Not often' },
            { value: 1, label: 'Sometimes' },
            { value: 0, label: 'Most of the time' },
        ],
    },
    {
        id: 7,
        type: 'anxiety',
        question: 'I can sit at ease and feel relaxed:',
        options: [
            { value: 0, label: 'Definitely' },
            { value: 1, label: 'Usually' },
            { value: 2, label: 'Not often' },
            { value: 3, label: 'Not at all' },
        ],
    },
    {
        id: 8,
        type: 'depression',
        question: 'I feel as if I am slowed down:',
        options: [
            { value: 3, label: 'Nearly all the time' },
            { value: 2, label: 'Very often' },
            { value: 1, label: 'Sometimes' },
            { value: 0, label: 'Not at all' },
        ],
    },
    {
        id: 9,
        type: 'anxiety',
        question: "I get a sort of frightened feeling like 'butterflies' in the stomach:",
        options: [
            { value: 0, label: 'Not at all' },
            { value: 1, label: 'Occasionally' },
            { value: 2, label: 'Quite often' },
            { value: 3, label: 'Very often' },
        ],
    },
    {
        id: 10,
        type: 'depression',
        question: 'I have lost interest in my appearance:',
        options: [
            { value: 3, label: 'Definitely' },
            { value: 2, label: "I don't take as much care as I should" },
            { value: 1, label: 'I may not take quite as much care' },
            { value: 0, label: 'I take just as much care as ever' },
        ],
    },
    {
        id: 11,
        type: 'anxiety',
        question: 'I feel restless as I have to be on the move:',
        options: [
            { value: 3, label: 'Very much indeed' },
            { value: 2, label: 'Quite a lot' },
            { value: 1, label: 'Not very much' },
            { value: 0, label: 'Not at all' },
        ],
    },
    {
        id: 12,
        type: 'depression',
        question: 'I look forward with enjoyment to things:',
        options: [
            { value: 0, label: 'As much as I ever did' },
            { value: 1, label: 'Rather less than I used to' },
            { value: 2, label: 'Definitely less than I used to' },
            { value: 3, label: 'Hardly at all' },
        ],
    },
    {
        id: 13,
        type: 'anxiety',
        question: 'I get sudden feelings of panic:',
        options: [
            { value: 3, label: 'Very often indeed' },
            { value: 2, label: 'Quite often' },
            { value: 1, label: 'Not very often' },
            { value: 0, label: 'Not at all' },
        ],
    },
    {
        id: 14,
        type: 'depression',
        question: 'I can enjoy a good book or radio or TV program:',
        options: [
            { value: 0, label: 'Often' },
            { value: 1, label: 'Sometimes' },
            { value: 2, label: 'Not often' },
            { value: 3, label: 'Very seldom' },
        ],
    },
];

const languageLabels = {
    eng: 'English',
    hin: 'Hindi',
    kan: 'Kannada',
};

const languageAliases = {
    en: 'eng',
    eng: 'eng',
    english: 'eng',
    hi: 'hin',
    hin: 'hin',
    hindi: 'hin',
    kn: 'kan',
    kan: 'kan',
    kannada: 'kan',
};

const hadsTranslations = {
    hin: {
        1: { question: 'मुझे तनाव या बेचैनी महसूस होती है:', options: ['अधिकांश समय', 'काफी समय', 'कभी-कभी', 'बिल्कुल नहीं'] },
        2: { question: 'मुझे अब भी वे चीजें अच्छी लगती हैं जो पहले लगती थीं:', options: ['बिल्कुल पहले जितनी', 'पहले जितनी नहीं', 'थोड़ी ही', 'लगभग बिल्कुल नहीं'] },
        3: { question: 'मुझे ऐसा डर लगता है जैसे कुछ बहुत बुरा होने वाला है:', options: ['बहुत स्पष्ट और काफी ज्यादा', 'हां, पर बहुत ज्यादा नहीं', 'थोड़ा, पर इससे ज्यादा चिंता नहीं होती', 'बिल्कुल नहीं'] },
        4: { question: 'मैं हंस सकता/सकती हूं और चीजों का मजेदार पहलू देख सकता/सकती हूं:', options: ['जितना हमेशा कर पाता/पाती था/थी', 'अब उतना नहीं', 'अब निश्चित रूप से काफी कम', 'बिल्कुल नहीं'] },
        5: { question: 'चिंता भरे विचार मेरे मन में आते रहते हैं:', options: ['बहुत अधिक समय', 'काफी समय', 'कभी-कभी, पर बहुत ज्यादा नहीं', 'केवल कभी-कभार'] },
        6: { question: 'मैं प्रसन्न महसूस करता/करती हूं:', options: ['बिल्कुल नहीं', 'अक्सर नहीं', 'कभी-कभी', 'अधिकांश समय'] },
        7: { question: 'मैं आराम से बैठकर सहज महसूस कर सकता/सकती हूं:', options: ['निश्चित रूप से', 'आमतौर पर', 'अक्सर नहीं', 'बिल्कुल नहीं'] },
        8: { question: 'मुझे लगता है कि मैं धीमा/धीमी हो गया/गई हूं:', options: ['लगभग हर समय', 'बहुत अक्सर', 'कभी-कभी', 'बिल्कुल नहीं'] },
        9: { question: 'मुझे पेट में घबराहट जैसी डर वाली भावना होती है:', options: ['बिल्कुल नहीं', 'कभी-कभार', 'काफी अक्सर', 'बहुत अक्सर'] },
        10: { question: 'मेरी अपनी दिखावट में रुचि कम हो गई है:', options: ['निश्चित रूप से', 'मैं जितना ध्यान रखना चाहिए उतना नहीं रखता/रखती', 'शायद थोड़ा कम ध्यान रखता/रखती हूं', 'मैं हमेशा की तरह ध्यान रखता/रखती हूं'] },
        11: { question: 'मुझे बेचैनी होती है जैसे मुझे चलते रहना पड़े:', options: ['बहुत ज्यादा', 'काफी ज्यादा', 'बहुत ज्यादा नहीं', 'बिल्कुल नहीं'] },
        12: { question: 'मैं चीजों का आनंद लेने की उम्मीद करता/करती हूं:', options: ['जितना हमेशा करता/करती था/थी', 'पहले से कुछ कम', 'पहले से निश्चित रूप से कम', 'लगभग बिल्कुल नहीं'] },
        13: { question: 'मुझे अचानक घबराहट के दौरे जैसे महसूस होते हैं:', options: ['बहुत अक्सर', 'काफी अक्सर', 'बहुत अक्सर नहीं', 'बिल्कुल नहीं'] },
        14: { question: 'मैं अच्छी किताब, रेडियो या टीवी कार्यक्रम का आनंद ले सकता/सकती हूं:', options: ['अक्सर', 'कभी-कभी', 'अक्सर नहीं', 'बहुत कम'] },
    },
    kan: {
        1: { question: 'ನನಗೆ ಒತ್ತಡ ಅಥವಾ ಅಶಾಂತಿ ಅನುಭವವಾಗುತ್ತದೆ:', options: ['ಹೆಚ್ಚಿನ ಸಮಯ', 'ಬಹಳಷ್ಟು ಸಮಯ', 'ಕೆಲವೊಮ್ಮೆ', 'ಎಲ್ಲವೂ ಇಲ್ಲ'] },
        2: { question: 'ಹಿಂದೆ ಇಷ್ಟಪಟ್ಟ ವಿಷಯಗಳನ್ನು ನಾನು ಇನ್ನೂ ಆನಂದಿಸುತ್ತೇನೆ:', options: ['ಹಿಂದಿನಷ್ಟೇ ಖಂಡಿತವಾಗಿ', 'ಅಷ್ಟು ಹೆಚ್ಚು ಅಲ್ಲ', 'ಸ್ವಲ್ಪ ಮಾತ್ರ', 'ಬಹುತೇಕ ಇಲ್ಲ'] },
        3: { question: 'ಏನೋ ಕೆಟ್ಟದ್ದು ಸಂಭವಿಸಲಿದೆ ಎಂಬ ಭಯದ ಅನುಭವ ಬರುತ್ತದೆ:', options: ['ಬಹಳ ಸ್ಪಷ್ಟವಾಗಿ ಮತ್ತು ತೀವ್ರವಾಗಿ', 'ಹೌದು, ಆದರೆ ತುಂಬಾ ಅಲ್ಲ', 'ಸ್ವಲ್ಪ, ಆದರೆ ಹೆಚ್ಚು ಚಿಂತೆಯಾಗುವುದಿಲ್ಲ', 'ಎಲ್ಲವೂ ಇಲ್ಲ'] },
        4: { question: 'ನಾನು ನಗಬಹುದು ಮತ್ತು ವಿಷಯಗಳ ಹಾಸ್ಯಭರಿತ ಭಾಗವನ್ನು ಕಾಣಬಹುದು:', options: ['ಹಿಂದಿನಂತೆ ಸಾಧ್ಯ', 'ಈಗ ಅಷ್ಟು ಅಲ್ಲ', 'ಈಗ ಖಂಡಿತವಾಗಿ ಬಹಳ ಕಡಿಮೆ', 'ಎಲ್ಲವೂ ಇಲ್ಲ'] },
        5: { question: 'ಚಿಂತೆಗಳ ಆಲೋಚನೆಗಳು ನನ್ನ ಮನಸ್ಸಿನಲ್ಲಿ ಬರುತ್ತವೆ:', options: ['ಬಹಳಷ್ಟು ಸಮಯ', 'ಹೆಚ್ಚಿನ ಸಮಯ', 'ಕೆಲವೊಮ್ಮೆ, ಆದರೆ ಹೆಚ್ಚು ಅಲ್ಲ', 'ಅಪರೂಪಕ್ಕೆ ಮಾತ್ರ'] },
        6: { question: 'ನಾನು ಸಂತೋಷವಾಗಿರುವಂತೆ ಅನುಭವಿಸುತ್ತೇನೆ:', options: ['ಎಲ್ಲವೂ ಇಲ್ಲ', 'ಹೆಚ್ಚಾಗಿ ಅಲ್ಲ', 'ಕೆಲವೊಮ್ಮೆ', 'ಹೆಚ್ಚಿನ ಸಮಯ'] },
        7: { question: 'ನಾನು ಆರಾಮವಾಗಿ ಕುಳಿತು ನೆಮ್ಮದಿಯಿಂದ ಇರಬಹುದು:', options: ['ಖಂಡಿತವಾಗಿ', 'ಸಾಮಾನ್ಯವಾಗಿ', 'ಹೆಚ್ಚಾಗಿ ಅಲ್ಲ', 'ಎಲ್ಲವೂ ಇಲ್ಲ'] },
        8: { question: 'ನಾನು ನಿಧಾನವಾಗಿರುವಂತೆ ಅನುಭವಿಸುತ್ತೇನೆ:', options: ['ಬಹುತೇಕ ಎಲ್ಲಾ ಸಮಯ', 'ಬಹಳಷ್ಟು ಬಾರಿ', 'ಕೆಲವೊಮ್ಮೆ', 'ಎಲ್ಲವೂ ಇಲ್ಲ'] },
        9: { question: 'ಹೊಟ್ಟೆಯಲ್ಲಿ ಗಾಬರಿಯಂತಹ ಭಯದ ಅನುಭವ ಬರುತ್ತದೆ:', options: ['ಎಲ್ಲವೂ ಇಲ್ಲ', 'ಅಪರೂಪಕ್ಕೆ', 'ಸಾಕಷ್ಟು ಬಾರಿ', 'ಬಹಳ ಬಾರಿ'] },
        10: { question: 'ನನ್ನ ರೂಪದ ಬಗ್ಗೆ ಆಸಕ್ತಿ ಕಡಿಮೆಯಾಗಿದೆ:', options: ['ಖಂಡಿತವಾಗಿ', 'ಬೇಕಾದಷ್ಟು ಗಮನ ಕೊಡುವುದಿಲ್ಲ', 'ಸ್ವಲ್ಪ ಕಡಿಮೆ ಗಮನ ಕೊಡಬಹುದು', 'ಹಿಂದಿನಂತೆ ಗಮನ ಕೊಡುತ್ತೇನೆ'] },
        11: { question: 'ನಾನು ಚಲಿಸುತ್ತಲೇ ಇರಬೇಕು ಎನ್ನುವ ಅಶಾಂತಿ ಅನುಭವಿಸುತ್ತೇನೆ:', options: ['ತುಂಬಾ ಹೆಚ್ಚು', 'ಸಾಕಷ್ಟು ಹೆಚ್ಚು', 'ತುಂಬಾ ಹೆಚ್ಚು ಅಲ್ಲ', 'ಎಲ್ಲವೂ ಇಲ್ಲ'] },
        12: { question: 'ಮುಂದಿನ ವಿಷಯಗಳನ್ನು ಆನಂದಿಸಲು ನಾನು ಕಾಯುತ್ತೇನೆ:', options: ['ಹಿಂದಿನಂತೆ', 'ಹಿಂದಿಗಿಂತ ಸ್ವಲ್ಪ ಕಡಿಮೆ', 'ಹಿಂದಿಗಿಂತ ಖಂಡಿತವಾಗಿ ಕಡಿಮೆ', 'ಬಹುತೇಕ ಇಲ್ಲ'] },
        13: { question: 'ನನಗೆ ಏಕಾಏಕಿ ಗಾಬರಿಯ ಭಾವನೆಗಳು ಬರುತ್ತವೆ:', options: ['ಬಹಳ ಬಾರಿ', 'ಸಾಕಷ್ಟು ಬಾರಿ', 'ಹೆಚ್ಚಾಗಿ ಅಲ್ಲ', 'ಎಲ್ಲವೂ ಇಲ್ಲ'] },
        14: { question: 'ಒಳ್ಳೆಯ ಪುಸ್ತಕ, ರೇಡಿಯೋ ಅಥವಾ ಟಿವಿ ಕಾರ್ಯಕ್ರಮವನ್ನು ನಾನು ಆನಂದಿಸಬಹುದು:', options: ['ಹೆಚ್ಚಾಗಿ', 'ಕೆಲವೊಮ್ಮೆ', 'ಹೆಚ್ಚಾಗಿ ಅಲ್ಲ', 'ಬಹಳ ಅಪರೂಪಕ್ಕೆ'] },
    },
};

const normalizeLanguage = (language, supportedLanguages = ['eng']) => {
    const normalized = languageAliases[String(language || '').trim().toLowerCase()] || 'eng';
    return supportedLanguages.includes(normalized) ? normalized : 'eng';
};

const getQuestionText = (question, language) => hadsTranslations[language]?.[question.id]?.question || question.question;

const getOptionText = (question, optionIndex, language) => hadsTranslations[language]?.[question.id]?.options?.[optionIndex] || question.options[optionIndex]?.label;

const getConsentText = (language, accountId) => {
    const assessmentName = accountId === "3" ? "KDQOL-36" : "EORTC";
    const text = {
        eng: {
            positiveMessage: "Thank you for completing the HADS assessment. It’s wonderful to see that you are managing your anxiety and depression well. Keep up the great work and continue your positive progress.",
            title: "Patient Consent for Study",
            intro: "I consent to participate in the Test Arm protocol which includes:",
            assessments: `Completing clinical assessments (HADS, PSQI, ${assessmentName})`,
            music: "Receiving personalized Echo music therapy",
            data: "Having my treatment data used for research purposes",
            startSession: "Start Session",
        },
        hin: {
            positiveMessage: "HADS मूल्यांकन पूरा करने के लिए धन्यवाद। यह देखकर अच्छा लगा कि आप अपनी चिंता और अवसाद को अच्छी तरह संभाल रहे हैं। अपनी सकारात्मक प्रगति जारी रखें।",
            title: "अध्ययन के लिए रोगी सहमति",
            intro: "मैं Test Arm प्रोटोकॉल में भाग लेने के लिए सहमति देता/देती हूं, जिसमें शामिल है:",
            assessments: `क्लिनिकल आकलन पूरा करना (HADS, PSQI, ${assessmentName})`,
            music: "व्यक्तिगत Echo म्यूजिक थेरेपी प्राप्त करना",
            data: "मेरे उपचार डेटा का शोध उद्देश्यों के लिए उपयोग होना",
            startSession: "सेशन शुरू करें",
        },
        kan: {
            positiveMessage: "HADS ಮೌಲ್ಯಮಾಪನವನ್ನು ಪೂರ್ಣಗೊಳಿಸಿದ್ದಕ್ಕೆ ಧನ್ಯವಾದಗಳು. ನೀವು ನಿಮ್ಮ ಆತಂಕ ಮತ್ತು ಖಿನ್ನತೆಯನ್ನು ಚೆನ್ನಾಗಿ ನಿರ್ವಹಿಸುತ್ತಿರುವುದು ಸಂತೋಷಕರ. ನಿಮ್ಮ ಸಕಾರಾತ್ಮಕ ಪ್ರಗತಿಯನ್ನು ಮುಂದುವರಿಸಿ.",
            title: "ಅಧ್ಯಯನಕ್ಕಾಗಿ ರೋಗಿಯ ಸಮ್ಮತಿ",
            intro: "Test Arm ಪ್ರೋಟೋಕಾಲ್‌ನಲ್ಲಿ ಭಾಗವಹಿಸಲು ನಾನು ಸಮ್ಮತಿಸುತ್ತೇನೆ. ಇದರಲ್ಲಿ ಇವುಗಳು ಸೇರಿವೆ:",
            assessments: `ಕ್ಲಿನಿಕಲ್ ಮೌಲ್ಯಮಾಪನಗಳನ್ನು ಪೂರ್ಣಗೊಳಿಸುವುದು (HADS, PSQI, ${assessmentName})`,
            music: "ವೈಯಕ್ತಿಕ Echo ಸಂಗೀತ ಚಿಕಿತ್ಸೆಯನ್ನು ಪಡೆಯುವುದು",
            data: "ನನ್ನ ಚಿಕಿತ್ಸೆ ಡೇಟಾವನ್ನು ಸಂಶೋಧನಾ ಉದ್ದೇಶಗಳಿಗೆ ಬಳಸುವುದು",
            startSession: "ಸೆಷನ್ ಪ್ರಾರಂಭಿಸಿ",
        },
    };

    return text[language] || text.eng;
};

function PreHADSScreening() {
    const navigate = useNavigate();
    const location = useLocation();
    const customer = location.state?.customer;
    const rawAccountId = localStorage.getItem("account_id");
    // Accounts 4/5 behave like accounts 2/3.
    const accountId = getEffectiveAccountId(rawAccountId);
    const isArcAccount = rawAccountId === "4" || rawAccountId === "5";

    const [currentPatient, setCurrentPatient] = useState(null);
    const [answers, setAnswers] = useState({});
    const [consent, setConsent] = useState(false);
    const [anxietyScore, setAnxietyScore] = useState(0);
    const [depressionScore, setDepressionScore] = useState(0);
    const [patients, setPatients] = useState([]);
    const [latestHADS, setLatestHADS] = useState(null);
    const [patient, setPatient] = useState(null);
const supportedLanguages = (accountId === "3" || isArcAccount) ? ['eng', 'hin', 'kan'] : ['eng'];    const [selectedLanguage, setSelectedLanguage] = useState(() => normalizeLanguage(customer?.preferred_language, supportedLanguages));
    const qualificationThreshold = (accountId === "3" || isArcAccount) ? 0 : 7;
    const qualifiesByScore = (score) => score > qualificationThreshold;

    useEffect(() => {
        const fetchCustomer = async () => {
            if (customer?.id) {
                try {
                    const data = await getCustomerById(customer?.id, rawAccountId);

                    setPatient(data);

                    // ✅ Log it for debugging
                    console.log("Fetched patient:", data);
                    // Normalize API data into your patient format
                    const normalizedPatient = {
                        id: data.id,
                        name: `${data?.firstname} ${data?.lastname}`,
                        age: calculateAge(data?.date_of_birth), // helper function
                        gender: data?.gender,
                        doctor: data?.assigned_doctor,
                        preferredLanguage: normalizeLanguage(data?.preferred_language, supportedLanguages),
                        hadsScreening: data?.hadsScreening || false, // assuming API may return null
                    };

                    setSelectedLanguage(normalizeLanguage(data?.preferred_language, supportedLanguages));
                    setPatients([normalizedPatient]); // store as array
                    selectPatientForScreening(normalizedPatient)
                } catch (error) {
                    console.error(error);
                }
            }
        };

        fetchCustomer();
    }, [customer?.id]);

    const calculateAge = (dob) => {
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        console.log("age", age)

        return age;
    };

    // Filtered arrays
    const unscreenedPatients = patients.filter((p) => !p.hadsScreening);
    const qualifiedPatients = patients.filter((p) => p.hadsScreening?.qualified);

    useEffect(() => {
        calculateScores();
    }, [answers]);

    const calculateScores = () => {
        let anxiety = 0;
        let depression = 0;

        hadsQuestions.forEach(q => {
            if (answers[q.id] !== undefined) {
                if (q.type === 'anxiety') {
                    anxiety += answers[q.id];
                } else {
                    depression += answers[q.id];
                }
            }
        });

        setAnxietyScore(anxiety);
        setDepressionScore(depression);
    };

    const handleAnswerChange = (questionId, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: parseInt(value),
        }));
    };

    const selectPatientForScreening = (patient) => {
        setCurrentPatient(patient);
        setAnswers({});
        setConsent(false);
        setAnxietyScore(0);
        setDepressionScore(0);
    };


    const submitHADS = async () => {
        const totalQuestions = 14;
        const answeredQuestions = Object.keys(answers).length;

        // 🚨 VALIDATE FIRST — ensure all questions are answered
        if (answeredQuestions < totalQuestions) {
            alert(`Please answer all ${totalQuestions} questions.`);
            return;
        }

        // 🚨 EARLY EXIT — Not eligible
        // if (anxietyScore < 7 || depressionScore < 7) {
        //     alert(
        //         "Thank you for completing the HADS assessment. It’s wonderful to see that you are managing your anxiety and depression well. Keep up the great work and continue your positive progress."
        //     );
        //     return;
        // }

        // if (!consent) {
        //     alert(
        //         "Thank you for completing the HADS assessment. As you have not provided consent for this treatment, we will not be proceeding further."
        //     );
        // }

        // if (!consent) {
        //     const confirmed = window.confirm(
        //         "Thank you for completing the HADS assessment. As you have not provided consent for this treatment, we will not be proceeding further. Do you want to continue?"
        //     );

        //     if (!confirmed) return; // ❌ stop here if user cancels
        // }

        if (!currentPatient) return;

        const qualified = qualifiesByScore(anxietyScore) || qualifiesByScore(depressionScore);
        let arm = null;
        let armAssignmentReason = "";

        // if (qualified) {
        //     if (currentPatient.age < 18) {
        //         arm = "control";
        //         armAssignmentReason =
        //             "Patient under 18 years old - automatically assigned to Control Arm";
        //     } else {
        //         if (consent) {
        //             arm = "test";
        //             armAssignmentReason =
        //                 "Patient is 18+ years old and provided consent - assigned to Test Arm";
        //         } else {
        //             arm = "control";
        //             armAssignmentReason =
        //                 "Patient is 18+ years old but did not provide consent - assigned to Control Arm";
        //         }
        //     }
        // }

        const hadsData = {
            anxiety: anxietyScore,
            depression: depressionScore,
            qualified,
            arm,
            armAssignmentReason,
            hasConsent: arm === "test",
            date: new Date().toLocaleDateString(),
        };

        setLatestHADS(hadsData);

        let resultMessage = `HADS Assessment Complete!\n\n`;
        resultMessage += `Patient: ${patient.firstname} ${patient.lastname}\n`;
        resultMessage += `Age: ${patient.age} years\n\n`;
        resultMessage += `Anxiety Score: ${anxietyScore}/21\n`;
        resultMessage += `Depression Score: ${depressionScore}/21\n\n`;

        // if (qualified) {
        //     resultMessage += `✓ QUALIFIED for treatment\n\n`;
        //     resultMessage += `Assignment: ${arm.toUpperCase()} ARM\n`;
        //     resultMessage += `Reason: ${armAssignmentReason}`;
        // } else {
        //     resultMessage += `✗ NOT QUALIFIED for treatment\n`;
        //     resultMessage += `(Both Anxiety and Depression scores ≤ 7)`;
        // }

        alert(resultMessage);

        const payload = {
            hads: true,
            consented: consent,
            anxiety_score: anxietyScore,
            depression_score: depressionScore,
        };

        try {
            await updateCustomer(customer.id, payload, rawAccountId);
            console.log("HADS data successfully updated for customer:", customer.id);
        } catch (error) {
            console.error("Error updating HADS data:", error);
        }

        setCurrentPatient(null);
    };

    const totalQuestions = hadsQuestions.length;
    const attempted = Object.keys(answers).length;
    const progress = (attempted / totalQuestions) * 100;
    const shouldShowConsent = latestHADS && latestHADS.qualified;
    const preferredLanguage = normalizeLanguage(patient?.preferred_language || customer?.preferred_language, supportedLanguages);
const showLanguageSwitch = (accountId === "3" || isArcAccount) && preferredLanguage !== 'eng';    const languageSwitchTarget = selectedLanguage === 'eng' ? preferredLanguage : 'eng';
    const consentText = getConsentText(selectedLanguage, accountId);
    const qualificationLabel = `>${qualificationThreshold}`;
    const normalLabel = qualificationThreshold === 0 ? '0 (Normal)' : `≤${qualificationThreshold} (Normal)`;

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box', px: { xs: 0, sm: 0 } }}>
            <FormContainer>
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        backgroundColor: "white",
                        pb: 2
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 0 },
                        }}
                    >
                        <Typography
                            variant="h6"
                            fontWeight={400}
                            sx={{ color: "#1f2937", fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                        >
                            Pre-HADS Screening (14 Questions)
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                            {showLanguageSwitch && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setSelectedLanguage(languageSwitchTarget)}
                                    sx={{
                                        bgcolor: "#7C9BFF",
                                        "&:hover": { bgcolor: "#6688F0" },
                                        borderRadius: { xs: 3, sm: 2 },
                                        px: { xs: 2.5, sm: 3 },
                                        py: { xs: 1, sm: 1 },
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: { xs: "0.85rem", sm: "0.875rem" },
                                        boxShadow: "0 3px 10px rgba(124, 155, 255, 0.3)",
                                        whiteSpace: "nowrap",
                                        minWidth: { xs: "120px", sm: "auto" },
                                        height: { xs: 42, sm: "auto" },
                                        width: { xs: '100%', sm: 'auto' },
                                    }}
                                >
                                    {languageLabels[languageSwitchTarget]}
                                </Button>
                            )}

                            <Button
                                variant="contained"
                               
                                onClick={() => navigate("/patients")}
                                sx={{
                                    bgcolor: "#4A90E2",
                                    "&:hover": { bgcolor: "#357ABD" },
                                    borderRadius: { xs: 3, sm: 2 },
                                    px: { xs: 2.5, sm: 3 },
                                    py: { xs: 1, sm: 1 },
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: { xs: "0.85rem", sm: "0.875rem" },
                                    boxShadow: {
                                        xs: "0 4px 12px rgba(74, 144, 226, 0.3)",
                                        sm: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                    },
                                    whiteSpace: "nowrap",
                                    minWidth: { xs: "120px", sm: "auto" },
                                    height: { xs: 42, sm: "auto" },
                                    width: { xs: '100%', sm: 'auto' },
                                    transition: "all 0.2s ease",
                                    "&:active": {
                                        transform: "scale(0.98)",
                                    },
                                }}
                            >
                                Back
                            </Button>
                        </Box>
                    </Box>

                    <Typography sx={{ color: '#666', lineHeight: 1.6, mb: 3, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        Screen patients using the Hospital Anxiety and Depression Scale (HADS).
                        Patients with Anxiety OR Depression scores greater than {qualificationThreshold} qualify for treatment.
                    </Typography>

                    <Typography variant="h6" sx={{ color: '#1a1238', fontSize: { xs: '1rem', sm: '1.1rem' }, wordBreak: 'break-word' }}>
                        Patient: {patient?.firstname} {patient?.lastname} |   Age: {patient?.age} | Gender: {patient?.gender?.charAt(0).toUpperCase() + patient?.gender?.slice(1)}
                        {patient?.assigned_doctor_name && ` | ${patient?.assigned_doctor_name}`}
                        <br />
                    </Typography>

                    <Box sx={{ mb: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 0.5,
                                color: "#374151",
                                fontWeight: 500,
                                textAlign: "right",
                            }}
                        >
                            {attempted}/{totalQuestions} answered
                        </Typography>

                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                height: 10,
                                borderRadius: 5,
                                backgroundColor: "#e5e7eb",
                                "& .MuiLinearProgress-bar": {
                                    backgroundColor: "#2563eb",
                                    borderRadius: 5,
                                },
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        overflowX: "hidden",
                    }}>

                    {currentPatient && (
                        <>

                            {hadsQuestions.map(question => (
                                <QuestionCard key={question.id}>
                                    <Typography
                                        sx={{
                                            mb: 2,
                                            color: "#1f2937",
                                            fontWeight: 600,
                                            fontSize: { xs: "1rem", sm: "1.1rem" },
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {question.id}. {getQuestionText(question, selectedLanguage)}
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: { xs: 1.5, sm: 2 },
                                            "& > *": {
                                                width: { xs: "100%", sm: "auto" },
                                            },
                                        }}
                                    >
                                        {question.options.map((option, optionIndex) => {
                                            const selected = answers[question.id] === option.value;

                                            return (
                                                <Box
                                                    key={option.value}
                                                    onClick={() =>
                                                        handleAnswerChange(question.id, option.value)
                                                    }
                                                    sx={{
                                                        background: selected ? "#667eea" : "white",
                                                        color: selected ? "white" : "inherit",
                                                        border: "1px solid #e5e7eb",
                                                        borderRadius: "10px",
                                                        p: { xs: 1.5, sm: 2 },
                                                        pr: { xs: 1.5, sm: 3 },
                                                        cursor: "pointer",
                                                        transition: "0.2s ease",
                                                        fontSize: { xs: "0.95rem", sm: "1rem" },
                                                        boxSizing: "border-box",
                                                        wordBreak: "break-word",
                                                        "&:hover": {
                                                            background: selected ? "#5a6de0" : "#f8f9ff",
                                                            boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                                                        },
                                                    }}
                                                >
                                                    {getOptionText(question, optionIndex, selectedLanguage)}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </QuestionCard>
                            ))}

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                                <ScoreDisplay>
                                    <Typography sx={{ color: '#667eea', fontSize: { xs: '2em', sm: '2.5em' }, fontWeight: 'bold' }}>
                                        {anxietyScore}
                                    </Typography>
                                    <Typography sx={{ color: '#666', fontSize: { xs: '0.8em', sm: '0.9em' } }}>
                                        Anxiety Score (0-21)
                                    </Typography>
                                    <Typography sx={{ mt: 1, fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                        {qualifiesByScore(anxietyScore) ? (
                                            <span style={{ color: '#4caf50' }}>✓ Qualifies ({qualificationLabel})</span>
                                        ) : (
                                            <span style={{ color: '#9e9e9e' }}>{normalLabel}</span>
                                        )}
                                    </Typography>
                                </ScoreDisplay>

                                <ScoreDisplay>
                                    <Typography sx={{ color: '#764ba2', fontSize: { xs: '2em', sm: '2.5em' }, fontWeight: 'bold' }}>
                                        {depressionScore}
                                    </Typography>
                                    <Typography sx={{ color: '#666', fontSize: { xs: '0.8em', sm: '0.9em' } }}>
                                        Depression Score (0-21)
                                    </Typography>
                                    <Typography sx={{ mt: 1, fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                        {qualifiesByScore(depressionScore) ? (
                                            <span style={{ color: '#4caf50' }}>✓ Qualifies ({qualificationLabel})</span>
                                        ) : (
                                            <span style={{ color: '#9e9e9e' }}>{normalLabel}</span>
                                        )}
                                    </Typography>
                                </ScoreDisplay>
                            </Box>

                            <SubmitButton onClick={submitHADS}>
                                Submit HADS Assessment
                            </SubmitButton>
                        </>
                    )}

                {latestHADS && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 3,
                            flexDirection: { xs: 'column', sm: 'row' },
                        }}
                    >
                        {/* Patient Info Box */}
                        <Box
                            sx={{
                                background: "#f8f9ff",
                                p: 2,
                                borderRadius: "10px",
                                flex: { xs: '1 1 100%', sm: '1 1 45%' },
                                minWidth: { xs: '100%', sm: 200 },
                                boxSizing: 'border-box',
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            <Typography variant="h6" sx={{ color: "#1a1238", mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' }, wordBreak: 'break-word' }}>
                                Patient: {patient?.firstname} {patient?.lastname}
                            </Typography>
                            <Typography sx={{ color: "#666", fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Age: {patient.age} | Gender: {patient.gender?.charAt(0).toUpperCase() + patient.gender?.slice(1)}
                                {patient?.assigned_doctor_name && ` | ${patient?.assigned_doctor_name}`}
                                <br />
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                <strong>Preferred Language:</strong>{" "}
                                {{
                                    eng: "English",
                                    hin: "Hindi",
                                    tel: "Telugu",
                                    ben: "Bengali",
                                    kan: "Kannada",
                                }[patient.preferred_language] || patient.preferred_language}
                            </Typography>
                        </Box>

                        {/* Score Section */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: { xs: 2, sm: 4 },
                                flex: { xs: '1 1 100%', sm: '1 1 45%' },
                                justifyContent: "center",
                                minWidth: { xs: '100%', sm: 300 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            <ScoreDisplay>
                                <Typography sx={{ color: "#667eea", fontSize: { xs: "2em", sm: "2.5em" }, fontWeight: "bold" }}>
                                    {anxietyScore}
                                </Typography>
                                <Typography sx={{ color: "#666", fontSize: { xs: "0.8em", sm: "0.9em" } }}>
                                    Anxiety Score (0-21)
                                </Typography>
                                <Typography sx={{ mt: 1, fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                    {qualifiesByScore(anxietyScore) ? (
                                        <span style={{ color: "#4caf50" }}>✓ Qualifies ({qualificationLabel})</span>
                                    ) : (
                                        <span style={{ color: "#9e9e9e" }}>{normalLabel}</span>
                                    )}
                                </Typography>
                            </ScoreDisplay>

                            <ScoreDisplay>
                                <Typography sx={{ color: "#764ba2", fontSize: { xs: "2em", sm: "2.5em" }, fontWeight: "bold" }}>
                                    {depressionScore}
                                </Typography>
                                <Typography sx={{ color: "#666", fontSize: { xs: "0.8em", sm: "0.9em" } }}>
                                    Depression Score (0-21)
                                </Typography>
                                <Typography sx={{ mt: 1, fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                    {qualifiesByScore(depressionScore) ? (
                                        <span style={{ color: "#4caf50" }}>✓ Qualifies ({qualificationLabel})</span>
                                    ) : (
                                        <span style={{ color: "#9e9e9e" }}>{normalLabel}</span>
                                    )}
                                </Typography>
                            </ScoreDisplay>
                        </Box>
                    </Box>
                )}

                {latestHADS && (
                    <>
                        {/* 1️⃣ If scores do not qualify → positive message */}
                        {!shouldShowConsent ? (
                            <Box sx={{ display: "flex", justifyContent: "center", mt: 2, px: { xs: 1, sm: 0 } }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        lineHeight: 1.6,
                                        color: "text.secondary",
                                        maxWidth: "600px",
                                        textAlign: { xs: 'center', sm: 'left' },
                                    }}
                                >
                                    {consentText.positiveMessage}
                                </Typography>
                            </Box>
                        ) :

                            /* 2️⃣ If consent is NOT given → show consent message */
                            consent === false ? (
                                // <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                                //     <Typography
                                //         variant="body1"
                                //         sx={{
                                //             fontSize: "14px",
                                //             fontWeight: 500,
                                //             lineHeight: 1.6,
                                //             color: "text.secondary",
                                //             maxWidth: "600px"
                                //         }}
                                //     >
                                //         Thank you for completing the HADS assessment. As you have not provided
                                //         consent for this treatment, we will not be proceeding further.
                                //     </Typography>
                                // </Box>
                                <Box
                                    sx={{
                                        background: '#fff3e0',
                                        border: '2px solid #ff9800',
                                        borderRadius: '10px',
                                        p: { xs: 1.5, sm: 3 },
                                        mb: 2,
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={consent}
                                                sx={{
                                                    p: { xs: 0.5, sm: 1 },
                                                    alignSelf: 'flex-start',
                                                }}
                                                onChange={async (e) => {
                                                    const checked = e.target.checked;
                                                    setConsent(checked);

                                                    if (checked) {
                                                        const payload = { consented: true, hads: true, anxiety_score: anxietyScore, depression_score: depressionScore, };

                                                        try {
                                                            await updateCustomer(customer.id, payload, rawAccountId);
                                                            console.log("Consent updated successfully");
                                                        } catch (error) {
                                                            console.error("Error updating consent:", error);
                                                        }
                                                    }
                                                }}
                                            />
                                        }
                                        label={
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 600, color: '#1a1238', fontSize: { xs: '1rem', sm: '1.1em' } }}>
                                                    {consentText.title}
                                                </Typography>
                                                <Typography sx={{ color: '#666', mt: 1, lineHeight: 1.6, fontSize: { xs: '0.85rem', sm: '0.95em' }, wordBreak: 'break-word' }}>
                                                    {consentText.intro}<br />
                                                    • {consentText.assessments}<br />
                                                    • {consentText.music}<br />
                                                    • {consentText.data}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{
                                            alignItems: 'flex-start',
                                            p: { xs: 1.25, sm: 2 },
                                            background: 'white',
                                            border: '2px solid #e0e0e0',
                                            borderRadius: '10px',
                                            mr: 0,
                                            ml: 0,
                                            width: '100%',
                                            maxWidth: '100%',
                                            boxSizing: 'border-box',
                                            gap: { xs: 0.5, sm: 1 },
                                            '& .MuiFormControlLabel-label': {
                                                width: '100%',
                                                minWidth: 0,
                                            },
                                        }}
                                    />
                                </Box>
                            ) :

                                /* 3️⃣ Else (scores high + consent true) → show Start Session */
                                (
                                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() =>
                                                navigate("/treatmentsessions", { state: { customer: patient } })
                                            }
                                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                                        >
                                            {consentText.startSession}
                                        </Button>
                                    </Box>
                                )}
                    </>
                )}
                </Box>

            </FormContainer>

            {qualifiedPatients.length > 0 && (
                <FormContainer>
                    <Typography variant="h5" sx={{ color: '#1a1238', mb: 3, fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>
                        Qualified Patients for Treatment
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(320px, 1fr))' }, gap: { xs: 2, sm: 3 } }}>
                        <Box sx={{ background: '#f8f9ff', borderRadius: '15px', p: { xs: 2, sm: 3 }, borderTop: '5px solid #ff9800', boxSizing: 'border-box' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                <ArmBadge arm="control" label="CONTROL ARM" />
                                <Typography component="span" sx={{ color: '#666', fontSize: '0.9em' }}>
                                    (General Music Only)
                                </Typography>
                            </Box>

                            {qualifiedPatients.filter(p => p.hadsScreening.arm === 'control').map(patient => (
                                <Box key={patient.id} sx={{ background: 'white', p: 2, borderRadius: '10px', mb: 2, boxSizing: 'border-box', wordBreak: 'break-word' }}>
                                    <Typography sx={{ fontWeight: 600, color: '#1a1238' }}>{patient.name}</Typography>
                                    <Typography sx={{ color: '#666', fontSize: '0.9em', mt: 0.5 }}>
                                        Anxiety: {patient.hadsScreening.anxiety} | Depression: {patient.hadsScreening.depression}<br />
                                        Screened: {patient.hadsScreening.date}<br />
                                        {patient.age < 18 ? (
                                            <span style={{ color: '#ff9800', fontWeight: 600 }}>• Under 18 - Auto-assigned</span>
                                        ) : (
                                            <span style={{ color: '#666' }}>• Age 18+ - No consent provided</span>
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ background: '#f8f9ff', borderRadius: '15px', p: { xs: 2, sm: 3 }, borderTop: '5px solid #4caf50', boxSizing: 'border-box' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                <ArmBadge arm="test" label="TEST ARM" />
                                <Typography component="span" sx={{ color: '#666', fontSize: '0.9em' }}>
                                    (Assessments + Echo Music)
                                </Typography>
                            </Box>

                            {qualifiedPatients.filter(p => p.hadsScreening.arm === 'test').map(patient => (
                                <Box key={patient.id} sx={{ background: 'white', p: 2, borderRadius: '10px', mb: 2, boxSizing: 'border-box', wordBreak: 'break-word' }}>
                                    <Typography sx={{ fontWeight: 600, color: '#1a1238' }}>{patient.name}</Typography>
                                    <Typography sx={{ color: '#4caf50', fontSize: '0.9em' }}>✓ Consented</Typography>
                                    <Typography sx={{ color: '#666', fontSize: '0.9em', mt: 0.5 }}>
                                        Anxiety: {patient.hadsScreening.anxiety} | Depression: {patient.hadsScreening.depression}<br />
                                        Screened: {patient.hadsScreening.date}<br />
                                        <span style={{ color: '#4caf50', fontWeight: 600 }}>• Age 18+ with informed consent</span>
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </FormContainer>
            )}
        </Box>
    );
}

export default PreHADSScreening;
